import type { UWidgetSpec, NormalizedMapping, ReferenceLineOption, AxisFormatOption, ChartSeriesOption, LineStyleKeyword } from '../core/types.js';
import { formatValue, parseDateText } from '../core/format.js';
import { normalizeMapping } from '../core/normalize.js';
import { isDateLikeString } from '../core/utils.js';
import { infer } from '../core/infer.js';

interface ConditionalStyleRule {
  field: string;
  above?: number;
  below?: number;
  color: string;
  symbol?: string;
  symbolSize?: number;
}

function matchConditionalStyle(
  row: Record<string, unknown>,
  rules: ConditionalStyleRule[],
): ConditionalStyleRule | undefined {
  for (const rule of rules) {
    const raw = row[rule.field];
    if (raw === undefined || raw === null) continue;
    const val = Number(raw);
    if (Number.isNaN(val)) continue;
    const aboveOk = rule.above === undefined || val > rule.above;
    const belowOk = rule.below === undefined || val < rule.below;
    if (aboveOk && belowOk) return rule;
  }
  return undefined;
}

/**
 * Translate a u-widget spec into an ECharts option object.
 * This is a pure function with no DOM or ECharts dependency.
 *
 * Supports `options.echarts` passthrough: any key-value pairs in
 * `options.echarts` are recursively deep-merged into the generated
 * ECharts option (nested objects merge; `series`, `xAxis` and `yAxis` arrays merge element by element —
 * `series: [{ type: 'line' }]` changes the first generated series rather than replacing it; other arrays
 * replace), allowing full
 * customization — including axis control such as
 * `options.echarts.xAxis.axisLabel` on top of the builder's category data.
 *
 * Limitation: passthrough merges option objects only — it does not register
 * the ECharts component modules those options require. Keys like `dataZoom`,
 * `toolbox`, or `title` (see UNREGISTERED_COMPONENT_KEYS) are merged but stay
 * inactive at runtime, and a one-time console warning is emitted. For heavy
 * interactions (zoom/pan/toolbox), use `@iyulab/flex-chart` instead.
 *
 * Pass `size` (the canvas in pixels) to lay the chart out for it: below about 320×260 a compact
 * layout replaces ECharts' default margins, which otherwise leave a small canvas almost no plot.
 * `<uw-chart>` passes its own size and rebuilds when the layout changes.
 */
export function toEChartsOption(spec: UWidgetSpec, size?: ChartSize): Record<string, unknown> {
  const widget = spec.widget;
  const data = spec.data;
  const mapping = spec.mapping ? normalizeMapping(spec.mapping) : undefined;
  const options = spec.options ?? {};

  if (!data) return {};

  let result: Record<string, unknown>;

  switch (widget) {
    case 'chart.bar':
      result = buildCartesian(data, mapping, 'bar', options);
      break;
    case 'chart.line':
      result = buildCartesian(data, mapping, 'line', options);
      break;
    case 'chart.area':
      result = buildCartesian(data, mapping, 'line', { ...options, _area: true });
      break;
    case 'chart.pie':
      result = buildPie(data, mapping, options);
      break;
    case 'chart.scatter':
      result = buildScatter(data, mapping, options);
      break;
    case 'chart.radar':
      result = buildRadar(data, mapping, options);
      break;
    case 'chart.heatmap':
      result = buildHeatmap(data, mapping, options);
      break;
    case 'chart.box':
      result = buildBoxplot(data, mapping, options);
      break;
    case 'chart.funnel':
      result = buildFunnel(data, mapping, options);
      break;
    case 'chart.waterfall':
      result = buildWaterfall(data, mapping, options);
      break;
    case 'chart.treemap':
      result = buildTreemap(data, options);
      break;
    case 'chart.histogram':
      result = buildHistogram(data, mapping, options);
      break;
    case 'chart.gantt':
      result = buildGantt(data, mapping, options);
      break;
    default:
      return {};
  }

  // Apply chart-level options
  if (options.legend === false && result.legend) {
    result.legend = { ...result.legend as Record<string, unknown>, show: false };
  }

  if (options.grid === false) {
    // Hide grid lines on axes (handles both single axis and array)
    const hideGrid = (axis: unknown): unknown => {
      if (!axis || typeof axis !== 'object') return axis;
      if (Array.isArray(axis)) return axis.map(hideGrid);
      return { ...axis as Record<string, unknown>, splitLine: { show: false } };
    };
    if (result.xAxis) result.xAxis = hideGrid(result.xAxis);
    if (result.yAxis) result.yAxis = hideGrid(result.yAxis);
  }

  if (options.animate === false) {
    result.animation = false;
  }

  if (Array.isArray(options.colors) && options.colors.length > 0) {
    result.color = options.colors;
  }

  // Apply axis format options
  const locale = options.locale as string | undefined;
  applyAxisFormat(result, 'xAxis', options.xFormat as AxisFormatOption | undefined, locale);
  applyAxisFormat(result, 'yAxis', options.yFormat as AxisFormatOption | undefined, locale);

  // Semantic aliases: valueAxisFormat / categoryAxisFormat route to the value or category
  // axis regardless of horizontal orientation. In horizontal bar/line/area, the value axis
  // is xAxis and the category axis is yAxis — the reverse of vertical orientation.
  const isCartesian = widget === 'chart.bar' || widget === 'chart.line' || widget === 'chart.area';
  if (isCartesian && (options.valueAxisFormat || options.categoryAxisFormat)) {
    const horizontal = !!options.horizontal;
    const valueKey: 'xAxis' | 'yAxis' = horizontal ? 'xAxis' : 'yAxis';
    const catKey: 'xAxis' | 'yAxis' = horizontal ? 'yAxis' : 'xAxis';
    applyAxisFormat(result, valueKey, options.valueAxisFormat as AxisFormatOption | undefined, locale);
    applyAxisFormat(result, catKey, options.categoryAxisFormat as AxisFormatOption | undefined, locale);
  }

  // A value axis no format option reached reads its numbers in the widget locale — ECharts writes
  // English grouping and a dot decimal («50,000» reads as fifty on a German page).
  applyLocaleNumberDefaults(result, locale);

  // valueAxisZero: false — the value axis spans the data instead of always reaching zero, for a series that
  // moves far from zero (a control chart around its centre line, a process temperature). ECharts calls it `scale`.
  if (isCartesian && options.valueAxisZero === false) {
    const valueKey: 'xAxis' | 'yAxis' = options.horizontal ? 'xAxis' : 'yAxis';
    const spanData = (axis: unknown): unknown =>
      Array.isArray(axis) ? axis.map(spanData) : axis && typeof axis === 'object' ? { ...(axis as Record<string, unknown>), scale: true } : axis;
    if (result[valueKey]) result[valueKey] = spanData(result[valueKey]);
  }

  if (isCartesian) reachReferenceLines(result, options);

  // Axis config placed at options top-level (instead of options.echarts) is dropped — warn.
  warnMisplacedAxisOptions(options);

  if (chartLayoutKey(size)) applyCompactLayout(result, widget, options, size!);

  // Apply echarts passthrough: recursive deep-merge onto the generated option.
  const passthrough = options.echarts as Record<string, unknown> | undefined;
  if (passthrough && typeof passthrough === 'object') {
    warnUnregisteredComponentKeys(passthrough);
    result = deepMerge(result, passthrough, ELEMENTWISE_KEYS);
  }

  // Guard series-type false affordance: catches both options.series[i].type
  // overrides and options.echarts.series passthrough at the final assembly point.
  warnUnregisteredSeriesTypes(result.series);

  return result;
}

/** The pixel size of the canvas a chart is drawn into. */
export interface ChartSize {
  width: number;
  height: number;
}

/**
 * Below these sizes ECharts' default layout leaves no room for the plot — the grid alone reserves
 * 65px above and 80px below, so a 140px-tall line chart drew a plot about 0px high with its value
 * labels stacked on each other. Charts this small get a compact layout instead.
 */
const COMPACT_MAX_HEIGHT = 260;
const COMPACT_MAX_WIDTH = 320;
/** Under this height a legend costs more plot than it explains; tooltips still name each series. */
const LEGEND_MIN_HEIGHT = 140;
/** A side legend next to a pie or funnel needs at least this much width to leave room for the shape. */
const SIDE_LEGEND_MIN_WIDTH = 280;

/**
 * Which compact layout a canvas size calls for, as a stable key — `''` when none does. A host that
 * re-renders on resize compares keys to rebuild the option only when the layout actually changes.
 */
export function chartLayoutKey(size: ChartSize | undefined): string {
  if (!size || size.width <= 0 || size.height <= 0) return '';
  if (size.height >= COMPACT_MAX_HEIGHT && size.width >= COMPACT_MAX_WIDTH) return '';
  return [
    'compact',
    size.height < LEGEND_MIN_HEIGHT ? 'no-legend' : 'legend',
    size.width < SIDE_LEGEND_MIN_WIDTH ? 'narrow' : 'wide',
    size.height < 160 ? 'short' : 'tall',
  ].join(':');
}

const COMPACT_LEGEND = {
  itemWidth: 12,
  itemHeight: 8,
  itemGap: 8,
  textStyle: { fontSize: 11 },
};

/**
 * Re-lay a built option for a small canvas: tight margins that still contain the axis labels,
 * fewer value ticks, overlapping category labels dropped, legends shrunk or hidden, and pie or
 * funnel labels moved off the outside of the shape (where they ran past the canvas edge).
 * Runs before the `options.echarts` passthrough, so anything the spec sets there still wins.
 */
function applyCompactLayout(
  result: Record<string, unknown>,
  widget: string,
  options: Record<string, unknown>,
  size: ChartSize,
): void {
  const showLegend = size.height >= LEGEND_MIN_HEIGHT;
  const sideLegend = showLegend && size.width >= SIDE_LEGEND_MIN_WIDTH;
  const legend = result.legend as Record<string, unknown> | undefined;
  const legendVisible = !!legend && legend.show !== false;

  if (result.xAxis && result.yAxis) {
    const splitNumber = size.height < 160 ? 2 : 3;
    const compactAxis = (axis: unknown): unknown => {
      if (!axis || typeof axis !== 'object') return axis;
      if (Array.isArray(axis)) return axis.map(compactAxis);
      const a = axis as Record<string, unknown>;
      const labels = (a.axisLabel as Record<string, unknown> | undefined) ?? {};
      return a.type === 'category'
        ? { ...a, axisLabel: { ...labels, hideOverlap: true } }
        : { ...a, splitNumber, axisLabel: { ...labels, hideOverlap: true } };
    };
    result.xAxis = compactAxis(result.xAxis);
    result.yAxis = compactAxis(result.yAxis);

    const keepLegend = legendVisible && showLegend;
    if (legend) result.legend = keepLegend ? { ...legend, top: 0, ...COMPACT_LEGEND } : { ...legend, show: false };
    if (result.visualMap) result.visualMap = { ...(result.visualMap as Record<string, unknown>), show: false };
    // A mark line's label (a reference line, gantt's makespan) sits above the plot's top edge.
    const markLineLabel = (result.series as Record<string, unknown>[] | undefined)?.some((s) => s.markLine) ? 14 : 0;
    // 'same' + 'all': the rect below is the outer bound, and the axis labels and names are fitted inside it.
    result.grid = {
      ...(result.grid as Record<string, unknown> | undefined),
      top: (keepLegend ? 28 : 12) + markLineLabel,
      right: 12,
      bottom: 8,
      left: 8,
      outerBoundsMode: 'same',
      outerBoundsContain: 'all',
    };
    return;
  }

  if (widget === 'chart.pie' || widget === 'chart.funnel') {
    if (legend) {
      result.legend = legendVisible && sideLegend
        ? { ...legend, orient: 'vertical', left: 4, top: 'middle', ...COMPACT_LEGEND }
        : { ...legend, show: false };
    }
    const offset = legendVisible && sideLegend;
    result.series = (result.series as Record<string, unknown>[]).map((s) => {
      if (widget === 'chart.pie') {
        const outsideLabels = options.showLabel !== false;
        return {
          ...s,
          radius: options.donut ? ['42%', '72%'] : '72%',
          center: [offset ? '64%' : '50%', '50%'],
          // Outside labels and their leader lines need room the canvas no longer has.
          ...(outsideLabels ? { label: { show: false }, labelLine: { show: false } } : {}),
        };
      }
      return { ...s, top: 8, bottom: 8, right: 8, left: offset ? '38%' : 8 };
    });
    return;
  }

  if (widget === 'chart.radar') {
    const keepLegend = legendVisible && showLegend;
    if (legend) result.legend = keepLegend ? { ...legend, top: 0, ...COMPACT_LEGEND } : { ...legend, show: false };
    // With a legend on top, the chart moves down so the top axis name clears it.
    const radar = result.radar as Record<string, unknown>;
    result.radar = {
      ...radar,
      radius: keepLegend ? '56%' : '62%',
      center: ['50%', keepLegend ? '58%' : '50%'],
      axisName: { fontSize: 10 },
    };
    return;
  }

  if (widget === 'chart.treemap') {
    result.series = (result.series as Record<string, unknown>[]).map((s) => ({
      ...s, top: 0, bottom: 0, left: 0, right: 0, breadcrumb: { show: false },
    }));
  }
}

/**
 * Top-level ECharts option keys whose component modules u-widgets does NOT
 * register (tree-shakeable build — see echarts.use([...]) in uw-chart.ts).
 * Passing them via `options.echarts` merges the option but the feature stays
 * inactive at runtime, so we warn to avoid a false affordance.
 * Heavy interactions (zoom/pan/toolbox) belong to @iyulab/flex-chart.
 */
const UNREGISTERED_COMPONENT_KEYS = new Set([
  'title', 'dataZoom', 'toolbox', 'brush', 'timeline', 'calendar',
  'graphic', 'dataset', 'polar', 'geo', 'singleAxis', 'parallel',
  'parallelAxis', 'axisPointer', 'aria',
]);

const warnedKeys = new Set<string>();

const warnedMisplacedAxis = new Set<string>();

/**
 * Axis config belongs under `options.echarts`, which is deep-merged onto the
 * builder-generated axis. A bare top-level `options.xAxis` / `options.yAxis` is
 * NOT merged and is silently dropped — several consumers passed `options.xAxis.name`
 * for months without effect. Surface the misplacement (warn-once per axis) with the
 * exact corrected path. Same false-affordance guard class as UNREGISTERED_COMPONENT_KEYS.
 */
function warnMisplacedAxisOptions(options: Record<string, unknown>): void {
  for (const axis of ['xAxis', 'yAxis'] as const) {
    if (options[axis] == null || warnedMisplacedAxis.has(axis)) continue;
    warnedMisplacedAxis.add(axis);
    console.warn(
      `[@iyulab/u-widgets] options.${axis} is ignored — axis config must go under ` +
      `options.echarts.${axis} (deep-merged onto the generated axis), e.g. ` +
      `options: { echarts: { ${axis}: { axisLabel: { interval: 0, rotate: 30 } } } }.`,
    );
  }
}

function warnUnregisteredComponentKeys(passthrough: Record<string, unknown>): void {
  for (const key of Object.keys(passthrough)) {
    if (!UNREGISTERED_COMPONENT_KEYS.has(key) || warnedKeys.has(key)) continue;
    warnedKeys.add(key);
    console.warn(
      `[@iyulab/u-widgets] options.echarts.${key} requires an ECharts component that ` +
      `u-widgets does not register (tree-shakeable build). The option is merged ` +
      `but will not work at runtime. For heavy interactions (zoom/pan/toolbox), ` +
      `use @iyulab/flex-chart instead.`,
    );
  }
}

/**
 * ECharts series `type` values whose chart modules u-widgets registers
 * (see echarts.use([...]) in uw-chart.ts). A series requesting any other type —
 * via `options.series[i].type` override or an `options.echarts.series[].type`
 * passthrough — merges into the option but renders nothing at runtime (ECharts
 * emits its own "Series <type> is used but not imported" warning), so we surface
 * a u-widgets message that names the fix. Symmetric with UNREGISTERED_COMPONENT_KEYS,
 * which guards the same false-affordance class for top-level component keys.
 */
const REGISTERED_SERIES_TYPES = new Set([
  'bar', 'line', 'pie', 'scatter', 'radar',
  'heatmap', 'boxplot', 'funnel', 'treemap', 'custom',
]);

const warnedSeriesTypes = new Set<string>();

function warnUnregisteredSeriesTypes(series: unknown): void {
  const list = Array.isArray(series) ? series : series != null ? [series] : [];
  for (const s of list) {
    if (!s || typeof s !== 'object') continue;
    const type = (s as Record<string, unknown>).type;
    if (typeof type !== 'string' || REGISTERED_SERIES_TYPES.has(type) || warnedSeriesTypes.has(type)) continue;
    warnedSeriesTypes.add(type);
    console.warn(
      `[@iyulab/u-widgets] series type "${type}" requires an ECharts chart module that ` +
      `u-widgets does not register (tree-shakeable build). The series is merged ` +
      `but will not render at runtime. For advanced chart types, ` +
      `use @iyulab/flex-chart instead.`,
    );
  }
}

const LINE_STYLE_KEYWORDS: readonly LineStyleKeyword[] = ['solid', 'dashed', 'dotted'];

/**
 * `options.series[i].lineStyle` → an ECharts `lineStyle` object. A keyword (`'dashed'`) is the
 * same vocabulary `referenceLines[].style` takes; an object passes through. Anything else is
 * dropped with a dev warning — spread as-is, a string would become `{0: 'd', 1: 'a', …}` and the
 * line would stay solid with no sign why.
 */
function seriesLineStyle(value: unknown, index: number): Record<string, unknown> | undefined {
  if (value == null) return undefined;
  if (LINE_STYLE_KEYWORDS.includes(value as LineStyleKeyword)) return { type: value };
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  console.warn(`[@iyulab/u-widgets] chart: options.series[${index}].lineStyle ${JSON.stringify(value)} is ignored — use ${LINE_STYLE_KEYWORDS.join(', ')} or a lineStyle object`);
  return undefined;
}

/**
 * A reference line on the value axis is part of what the chart says — a contract limit, a target the data has
 * not reached yet — but ECharts sizes the axis to the data alone, so a line beyond it is not drawn at all. Where
 * one lies past the data, the axis is taken out to a round number just beyond it. Stacked series are left to
 * ECharts: their extent is the stack's sum, not any one series'.
 */
function reachReferenceLines(result: Record<string, unknown>, options: Record<string, unknown>): void {
  const key = options.horizontal ? 'xAxis' : 'yAxis';
  const lines = ((options.referenceLines as ReferenceLineOption[] | undefined) ?? [])
    .filter((rl) => rl.axis === key[0] && typeof rl.value === 'number')
    .map((rl) => rl.value as number);
  const series = (result.series as Record<string, unknown>[] | undefined) ?? [];
  if (!lines.length || series.some((s) => s.stack)) return;
  const data = series
    .flatMap((s) => (Array.isArray(s.data) ? s.data : []).map((p) => {
      const v = (p as { value?: unknown } | null)?.value ?? p;
      // An `[x, y]` point (a numeric x axis) — the value is y.
      return Array.isArray(v) ? v[1] : v;
    }))
    .filter((v): v is number => typeof v === 'number');
  const axis = [result[key]].flat()[0] as Record<string, unknown> | undefined;
  if (!data.length || !axis) return;
  const hi = Math.max(...lines), lo = Math.min(...lines), max = Math.max(...data), min = Math.min(...data);
  if (hi > max) axis.max = roundAway(hi + (hi - Math.min(0, min)) / 50, 1);
  if (lo < min && (axis.scale || lo < 0)) axis.min = roundAway(lo - (Math.max(0, max) - lo) / 50, -1);
}

/** A round number past `x` in `direction` (1 up, -1 down): to two significant figures, as axis labels read. */
function roundAway(x: number, direction: 1 | -1): number {
  if (x === 0) return 0;
  const step = 10 ** (Math.floor(Math.log10(Math.abs(x))) - 1);
  return (direction > 0 ? Math.ceil(x / step) : Math.floor(x / step)) * step;
}

/**
 * `options.referenceLines` → an ECharts `markLine` (placed on the first series).
 *
 * @param toX - Maps an `axis: 'x'` value to the axis coordinate (a histogram's bin index, a
 *   gantt's timestamp). Identity by default.
 * @param xLabelPosition - Where an x line's label sits; `'end'` unless the y axis is inverted.
 */
function buildMarkLine(
  refLines: ReferenceLineOption[],
  toX: (value: number | string) => unknown = (v) => v,
  xLabelPosition = 'end',
): Record<string, unknown> {
  return {
    silent: true,
    symbol: 'none',
    data: refLines.map((rl) => {
      const item: Record<string, unknown> = rl.axis === 'x' ? { xAxis: toX(rl.value) } : { yAxis: rl.value };
      if (rl.label) {
        item.name = rl.label;
        // A y line's label goes inside the plot, above the line's right end: at 'end' it sits outside the plot's
        // right edge, which the grid leaves no room for, and a label longer than the margin was cut off.
        item.label = { formatter: rl.label, position: rl.axis === 'x' ? xLabelPosition : 'insideEndTop' };
      }
      const lineStyle: Record<string, unknown> = {};
      if (rl.color) lineStyle.color = rl.color;
      if (rl.style) lineStyle.type = rl.style;
      if (Object.keys(lineStyle).length > 0) item.lineStyle = lineStyle;
      return item;
    }),
  };
}

function buildCartesian(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  type: string,
  options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  const xField = mapping?.x ?? guessStringField(data);
  const yFields = mapping?.y ?? [guessNumberField(data)].filter(Boolean) as string[];

  if (!xField || yFields.length === 0) return {};

  const categories = data.map((row) => String(row[xField] ?? ''));
  const horizontal = !!options.horizontal;
  // A numeric x axis — points at their x values, not evenly spaced categories: a profile over uneven times.
  const valueX = !horizontal && options.xAxisType === 'value';
  const point = (row: Record<string, unknown>, val: unknown) => {
    const x = row[xField];
    return valueX ? [x == null || x === '' ? null : Number(x), val] : val;
  };

  const seriesOverrides = Array.isArray(options.series) ? options.series as ChartSeriesOption[] : [];
  const condStyles = options.conditionalStyles as ConditionalStyleRule[] | undefined;

  const seriesItems = yFields.map((field, i) => {
    const s: Record<string, unknown> = {
      name: field,
      type,
      data: data.map((row) => {
        const val = row[field] ?? null;
        if (condStyles?.length) {
          const match = matchConditionalStyle(row, condStyles);
          if (match) {
            const item: Record<string, unknown> = { value: point(row, val), itemStyle: { color: match.color } };
            if (match.symbol) item.symbol = match.symbol;
            if (match.symbolSize) item.symbolSize = match.symbolSize;
            return item;
          }
        }
        return point(row, val);
      }),
    };
    if (options._area) s.areaStyle = {};
    if (options.smooth) s.smooth = true;
    if (options.step) s.step = options.step === true ? 'end' : options.step;

    // Apply per-series overrides from options.series[i]
    const override = seriesOverrides[i];
    if (override) {
      if (override.color) {
        s.itemStyle = { color: override.color };
        s.lineStyle = { ...(s.lineStyle as Record<string, unknown> ?? {}), color: override.color };
      }
      const lineStyle = seriesLineStyle(override.lineStyle, i);
      if (lineStyle) s.lineStyle = { ...(s.lineStyle as Record<string, unknown> ?? {}), ...lineStyle };
      if (override.symbol !== undefined) s.symbol = override.symbol;
      if (override.label) s.name = override.label;
      if (override.type) s.type = override.type;
      if (override.yAxisIndex !== undefined) s.yAxisIndex = override.yAxisIndex;
    }
    return s;
  });

  // A horizontal chart lists its categories top to bottom in the data's order, as text reads — ECharts would start
  // the category axis at the bottom, putting a ranking's first entry last.
  const catAxis: Record<string, unknown> = valueX
    // `scale`: an x value axis starts at the data, not at zero (years, timestamps).
    ? { type: 'value', scale: true }
    : { type: 'category', data: categories, ...(horizontal && { inverse: true }) };
  const valAxis: Record<string, unknown> = { type: 'value' };

  // Histogram mode: remove gaps between bars
  if (options.histogram) {
    catAxis.axisTick = { alignWithLabel: true };
    seriesItems.forEach((s) => { s.barCategoryGap = '0%'; });
  }

  // Detect if any series requires a secondary Y axis
  const needsDualYAxis = !horizontal && seriesItems.some((s) => (s.yAxisIndex as number) >= 1);
  let yAxisConfig: unknown;
  if (needsDualYAxis) {
    const secondAxis = Array.isArray(options.yAxis) && (options.yAxis as unknown[]).length >= 2
      ? (options.yAxis as Record<string, unknown>[])[1]
      : { type: 'value' };
    yAxisConfig = [valAxis, secondAxis];
  } else {
    yAxisConfig = horizontal ? catAxis : valAxis;
  }

  const result: Record<string, unknown> = {
    xAxis: horizontal ? valAxis : catAxis,
    yAxis: yAxisConfig,
    series: seriesItems,
    tooltip: { trigger: 'axis' },
  };

  if (yFields.length > 1) {
    const legendNames = seriesItems.map((s) => s.name as string);
    result.legend = { data: legendNames };
  }

  if (options.stack) {
    (result.series as Record<string, unknown>[]).forEach((s) => {
      s.stack = 'total';
    });
  }

  // Reference lines → ECharts markLine on first series
  const refLines = options.referenceLines as ReferenceLineOption[] | undefined;
  if (Array.isArray(refLines) && refLines.length > 0 && seriesItems.length > 0) {
    seriesItems[0].markLine = buildMarkLine(refLines);
  }

  return result;
}

function buildScatter(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  // For scatter, prefer two numeric fields for x/y
  const numFields = getNumberFields(data);
  const xField = mapping?.x ?? numFields[0];
  const yField = (mapping?.y ?? [numFields[1]])[0];
  const colorField = mapping?.color;
  const sizeField = mapping?.size;
  const opacityField = mapping?.opacity;

  if (!xField || !yField) return {};

  const result: Record<string, unknown> = {
    xAxis: { type: 'value' },
    yAxis: { type: 'value' },
    tooltip: { trigger: 'item' },
  };

  // Pre-compute opacity normalization range if needed
  let opacityMin = Infinity;
  let opacityMax = -Infinity;
  if (opacityField) {
    for (const row of data) {
      const v = Number(row[opacityField] ?? 0);
      if (v < opacityMin) opacityMin = v;
      if (v > opacityMax) opacityMax = v;
    }
  }
  const opacityRange = opacityMax - opacityMin || 1;

  const condStyles = options.conditionalStyles as ConditionalStyleRule[] | undefined;

  // Helper: build a data point — plain array or object with itemStyle for opacity/conditionalStyles
  const toPoint = (row: Record<string, unknown>): unknown => {
    const pt = [Number(row[xField] ?? 0), Number(row[yField] ?? 0)];
    if (sizeField) pt.push(Number(row[sizeField] ?? 0));

    if (condStyles?.length) {
      const match = matchConditionalStyle(row, condStyles);
      if (match) {
        const style: Record<string, unknown> = { color: match.color };
        if (opacityField) {
          const raw = Number(row[opacityField] ?? 0);
          style.opacity = Math.round((0.1 + 0.9 * ((raw - opacityMin) / opacityRange)) * 100) / 100;
        }
        const item: Record<string, unknown> = { value: pt, itemStyle: style };
        if (match.symbol) item.symbol = match.symbol;
        if (match.symbolSize) item.symbolSize = match.symbolSize;
        return item;
      }
    }

    if (opacityField) {
      const raw = Number(row[opacityField] ?? 0);
      const norm = 0.1 + 0.9 * ((raw - opacityMin) / opacityRange);
      return { value: pt, itemStyle: { opacity: Math.round(norm * 100) / 100 } };
    }
    return pt;
  };

  // symbolSize function that maps the third value to pixel radius
  const symbolSizeFn = sizeField
    ? (val: number[] | { value: number[] }) => {
        const arr = Array.isArray(val) ? val : val.value;
        const raw = arr[2] ?? 0;
        // Clamp to [4, 60] range — square root scale for area perception
        return Math.max(4, Math.min(60, Math.sqrt(raw) * 4));
      }
    : undefined;

  if (colorField) {
    // Group data by color field → separate series per group
    const groups = new Map<string, unknown[]>();
    data.forEach((row, rowIndex) => {
      const key = String(row[colorField] ?? 'unknown');
      if (!groups.has(key)) groups.set(key, []);
      // Split into series, a point's dataIndex is local to its group — carry the spec.data row.
      const point = toPoint(row);
      groups.get(key)!.push(Array.isArray(point) ? { value: point, rowIndex } : { ...(point as Record<string, unknown>), rowIndex });
    });
    const seriesItems: Record<string, unknown>[] = [];
    for (const [name, points] of groups) {
      const series: Record<string, unknown> = { name, type: 'scatter', data: points };
      if (symbolSizeFn) series.symbolSize = symbolSizeFn;
      seriesItems.push(series);
    }
    result.series = seriesItems;
    result.legend = { data: Array.from(groups.keys()) };
  } else {
    // Single series — all points same color
    const points = data.map((row) => toPoint(row));
    const series: Record<string, unknown> = { type: 'scatter', data: points };
    if (symbolSizeFn) series.symbolSize = symbolSizeFn;
    result.series = [series];
  }

  // Reference lines
  const refLines = options.referenceLines as ReferenceLineOption[] | undefined;
  if (Array.isArray(refLines) && refLines.length > 0) {
    const firstSeries = (result.series as Record<string, unknown>[])[0];
    firstSeries.markLine = buildMarkLine(refLines);
  }

  return result;
}

function buildPie(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  const labelField = mapping?.label ?? guessStringField(data);
  const valueField = mapping?.value ?? guessNumberField(data);

  if (!labelField || !valueField) return {};

  const pieData = data.map((row) => ({
    name: String(row[labelField] ?? ''),
    value: Number(row[valueField] ?? 0),
  }));

  const radius = options.donut ? ['40%', '70%'] : '50%';
  const seriesItem: Record<string, unknown> = {
    type: 'pie',
    radius,
    data: pieData,
    label: { overflow: 'truncate', width: 80 },
  };

  if (options.showLabel === false) {
    seriesItem.label = { show: false };
  }

  const result: Record<string, unknown> = {
    tooltip: { trigger: 'item' },
    legend: { orient: 'vertical', left: 'left' },
    series: [seriesItem],
  };

  // Donut center label
  const center = options.center as { label?: string; value?: string } | undefined;
  if (options.donut && center && (center.label || center.value)) {
    const children: Record<string, unknown>[] = [];
    if (center.label) {
      children.push({
        type: 'text',
        top: center.value ? -16 : 0,
        style: { text: center.label, fontSize: 13, fill: '#999', textAlign: 'center' },
      });
    }
    if (center.value) {
      children.push({
        type: 'text',
        top: center.label ? 8 : 0,
        style: { text: center.value, fontSize: 20, fontWeight: 'bold', fill: '#333', textAlign: 'center' },
      });
    }
    result.graphic = [{ type: 'group', left: 'center', top: 'middle', children }];
  }

  return result;
}

function buildRadar(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  _options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  const axisField = mapping?.axis ?? guessStringField(data);
  const yFields = mapping?.y ?? getNumberFields(data);

  if (!axisField || yFields.length === 0) return {};

  const indicator = data.map((row) => ({
    name: String(row[axisField] ?? ''),
  }));

  const series = yFields.map((field) => ({
    name: field,
    value: data.map((row) => Number(row[field] ?? 0)),
  }));

  return {
    tooltip: {},
    legend: { data: yFields },
    radar: { indicator },
    series: [
      {
        type: 'radar',
        data: series,
      },
    ],
  };
}

function buildHeatmap(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  // Determine x, y (category), value (number) fields
  const first = data[0] ?? {};
  const keys = Object.keys(first);
  const stringFields = keys.filter((k) => typeof first[k] === 'string');
  const numberFields = keys.filter((k) => typeof first[k] === 'number');

  const xField = mapping?.x ?? stringFields[0];
  // For heatmap, y is a category field (string), not a numeric series
  const yField = mapping?.y?.[0] ?? stringFields.find((k) => k !== xField);
  const valueField = mapping?.value ?? numberFields[0];

  if (!xField || !yField || !valueField) return {};

  // Extract unique categories preserving order of appearance
  const xCats: string[] = [];
  const yCats: string[] = [];
  const xSet = new Set<string>();
  const ySet = new Set<string>();

  for (const row of data) {
    const x = String(row[xField] ?? '');
    const y = String(row[yField] ?? '');
    if (!xSet.has(x)) { xSet.add(x); xCats.push(x); }
    if (!ySet.has(y)) { ySet.add(y); yCats.push(y); }
  }

  // Build index maps for O(1) lookup
  const xIndex = new Map<string, number>();
  xCats.forEach((v, i) => xIndex.set(v, i));
  const yIndex = new Map<string, number>();
  yCats.forEach((v, i) => yIndex.set(v, i));

  // Build [xIndex, yIndex, value] data
  const heatData: (number | null)[][] = [];
  let minVal = Infinity;
  let maxVal = -Infinity;

  for (const row of data) {
    const xi = xIndex.get(String(row[xField] ?? '')) ?? 0;
    const yi = yIndex.get(String(row[yField] ?? '')) ?? 0;
    const v = row[valueField] != null ? Number(row[valueField]) : null;
    heatData.push([xi, yi, v]);
    if (v != null) {
      if (v < minVal) minVal = v;
      if (v > maxVal) maxVal = v;
    }
  }

  // Handle edge case of empty/no-value data
  if (!isFinite(minVal)) { minVal = 0; maxVal = 1; }

  // Determine color range from options or use defaults
  const colorRange = (options.colorRange as string[]) ?? ['#313695', '#4575b4', '#74add1', '#abd9e9', '#fee090', '#fdae61', '#f46d43', '#d73027'];

  return {
    xAxis: { type: 'category', data: xCats, splitArea: { show: true } },
    yAxis: { type: 'category', data: yCats, splitArea: { show: true } },
    visualMap: {
      min: options.min != null ? Number(options.min) : minVal,
      max: options.max != null ? Number(options.max) : maxVal,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: colorRange },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: Record<string, unknown>) => {
        const d = params.data as number[];
        if (!d) return '';
        // The tooltip is HTML: category names come from the data, so they are escaped.
        const value = localeFormatters(options.locale as string | undefined).valueFormatter(d[2]);
        return `${escapeHtml(String(xCats[d[0]]))} × ${escapeHtml(String(yCats[d[1]]))}: ${value}`;
      },
    },
    grid: { bottom: 60 },
    series: [{
      type: 'heatmap',
      data: heatData,
      label: { show: options.showLabel !== false },
    }],
  };
}

function buildBoxplot(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  _options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data) || data.length === 0) return {};

  const first = data[0] ?? {};
  const keys = Object.keys(first);
  const stringFields = keys.filter((k) => typeof first[k] === 'string');
  const numberFields = keys.filter((k) => typeof first[k] === 'number');

  // Category field (x)
  const xField = mapping?.x ?? stringFields[0];

  // Stat fields: use mapping.y if provided (5 fields: min, q1, median, q3, max)
  // Otherwise, try well-known names, then fall back to first 5 number fields
  let statFields: string[];
  if (mapping?.y && mapping.y.length >= 5) {
    statFields = mapping.y.slice(0, 5);
  } else {
    const wellKnown = ['min', 'q1', 'median', 'q3', 'max'];
    const matched = wellKnown.filter((name) => numberFields.includes(name));
    statFields = matched.length === 5 ? matched : numberFields.slice(0, 5);
  }

  if (statFields.length < 5) return {};

  const categories = xField ? data.map((row) => String(row[xField] ?? '')) : undefined;

  const boxData = data.map((row) =>
    statFields.map((f) => Number(row[f] ?? 0)),
  );

  const result: Record<string, unknown> = {
    tooltip: { trigger: 'item' },
    series: [{
      type: 'boxplot',
      data: boxData,
    }],
  };

  if (categories) {
    result.xAxis = { type: 'category', data: categories };
    result.yAxis = { type: 'value' };
  } else {
    result.xAxis = { type: 'category' };
    result.yAxis = { type: 'value' };
  }

  return result;
}

// ── Helpers ──

function guessStringField(data: Record<string, unknown>[]): string | undefined {
  if (data.length === 0) return undefined;
  const first = data[0];
  return Object.keys(first).find((k) => typeof first[k] === 'string');
}

function guessNumberField(data: Record<string, unknown>[]): string | undefined {
  if (data.length === 0) return undefined;
  const first = data[0];
  return Object.keys(first).find((k) => typeof first[k] === 'number');
}

function buildFunnel(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  _options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  const labelField = mapping?.label ?? guessStringField(data as Record<string, unknown>[]);
  const valueField = (mapping?.value ?? guessNumberField(data as Record<string, unknown>[]));

  if (!labelField || !valueField) return {};

  const seriesData = (data as Record<string, unknown>[]).map((row) => ({
    name: String(row[labelField] ?? ''),
    value: Number(row[valueField] ?? 0),
  }));

  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { orient: 'vertical', left: 'left' },
    series: [{
      type: 'funnel',
      data: seriesData,
      sort: 'descending',
      gap: 2,
      label: { show: true, position: 'inside' },
    }],
  };
}

function buildWaterfall(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  _options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  const records = data as Record<string, unknown>[];
  const xField = mapping?.x ?? guessStringField(records);
  const yField = (mapping?.y ?? [guessNumberField(records)])[0];
  const totalField = mapping?.total;

  if (!xField || !yField) return {};

  const categories: string[] = [];
  const baseValues: number[] = [];
  const positiveValues: (number | null)[] = [];
  const negativeValues: (number | null)[] = [];
  const totalValues: (number | null)[] = [];
  let hasTotalRow = false;

  let running = 0;
  for (const row of records) {
    const label = String(row[xField] ?? '');
    const value = Number(row[yField] ?? 0);
    const isTotal = totalField != null && !!row[totalField];
    categories.push(label);

    if (isTotal) {
      // Absolute total bar: drawn from zero, and it resets the running total to its
      // own value (a leading total is an opening balance, a trailing total a closing
      // balance). Not added as a delta — that is the "2×total" bug this guards.
      hasTotalRow = true;
      baseValues.push(0);
      positiveValues.push(null);
      negativeValues.push(null);
      totalValues.push(value);
      running = value;
    } else if (value >= 0) {
      baseValues.push(running);
      positiveValues.push(value);
      negativeValues.push(null);
      totalValues.push(null);
      running += value;
    } else {
      baseValues.push(running + value);
      positiveValues.push(null);
      negativeValues.push(Math.abs(value));
      totalValues.push(null);
      running += value;
    }
  }

  const series: Record<string, unknown>[] = [
    {
      name: 'Base',
      type: 'bar',
      stack: 'waterfall',
      itemStyle: { borderColor: 'transparent', color: 'transparent' },
      emphasis: { itemStyle: { borderColor: 'transparent', color: 'transparent' } },
      data: baseValues,
    },
    {
      name: 'Positive',
      type: 'bar',
      stack: 'waterfall',
      data: positiveValues,
    },
    {
      name: 'Negative',
      type: 'bar',
      stack: 'waterfall',
      data: negativeValues,
    },
  ];

  // Only emit the Total series when total rows exist, keeping the pure
  // cumulative-delta waterfall byte-identical (non-breaking).
  if (hasTotalRow) {
    series.push({
      name: 'Total',
      type: 'bar',
      stack: 'waterfall',
      itemStyle: { color: '#607d8b' }, // neutral slate — visually distinct from increments
      data: totalValues,
    });
  }

  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: { type: 'category', data: categories },
    yAxis: { type: 'value' },
    series,
  };
}

function buildTreemap(
  data: unknown,
  _options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data)) return {};

  // Treemap data uses name/value/children directly — pass through to ECharts
  const seriesData = (data as Record<string, unknown>[]).map((item) => toTreeNode(item));

  return {
    tooltip: { trigger: 'item' },
    series: [{
      type: 'treemap',
      data: seriesData,
      leafDepth: 1,
      roam: false,
      label: { show: true, formatter: '{b}' },
    }],
  };
}

function toTreeNode(item: Record<string, unknown>): Record<string, unknown> {
  const node: Record<string, unknown> = {
    name: String(item.name ?? ''),
  };
  const hasChildren = Array.isArray(item.children);
  // 부모 노드에 value: 0을 명시하면 ECharts가 children 합산 대신 면적 0으로 렌더한다
  // (트리맵 전체가 빈 화면이 되는 무음 결함). value가 있을 때만 전달하고 합산은 ECharts에 맡긴다.
  if (item.value != null) {
    node.value = Number(item.value);
  } else if (!hasChildren) {
    node.value = 0;
  }
  if (hasChildren) {
    node.children = (item.children as Record<string, unknown>[]).map((child) => toTreeNode(child));
  }
  return node;
}

function buildHistogram(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  options: Record<string, unknown>,
): Record<string, unknown> {
  // Extract numeric values from flat array or object array
  let values: number[];
  if (!Array.isArray(data) || data.length === 0) return {};

  if (typeof data[0] === 'number') {
    values = data as number[];
  } else {
    const field = mapping?.value ?? guessNumberField(data as Record<string, unknown>[]);
    if (!field) return {};
    values = (data as Record<string, unknown>[]).map((row) => Number(row[field] ?? 0));
  }

  if (values.length === 0) return {};

  // Determine bin count: explicit or Sturges' rule
  const binCount = typeof options.bins === 'number'
    ? options.bins
    : Math.ceil(Math.log2(values.length) + 1);

  // The X axis domain must cover referenceLines too, not just the data \u2014 otherwise an
  // out-of-domain reference (e.g. a spec limit outside the observed sample range) has no
  // valid bin-index coordinate to draw at.
  const refLines = options.referenceLines as ReferenceLineOption[] | undefined;
  const refXValues = Array.isArray(refLines)
    ? refLines
        .filter((rl) => rl.axis === 'x' && typeof rl.value === 'number')
        .map((rl) => rl.value as number)
    : [];

  const min = Math.min(...values, ...refXValues);
  const max = Math.max(...values, ...refXValues);
  const range = max - min;
  const binWidth = range / binCount || 1;

  // Build bins
  const bins: number[] = new Array(binCount).fill(0);
  const labels: string[] = [];

  // Decimal places needed so adjacent bin boundaries (which differ by binWidth) don't
  // round to the same displayed label \u2014 fixed 1-decimal rounding showed "9.7\u20139.7" for
  // bins narrower than 0.1 (secondary repro).
  const labelDecimals = binWidth > 0 && binWidth < 1
    ? Math.min(10, Math.max(1, Math.ceil(-Math.log10(binWidth))))
    : 1;

  for (let i = 0; i < binCount; i++) {
    const lo = min + i * binWidth;
    const hi = min + (i + 1) * binWidth;
    const fmt = (n: number) => Number.isInteger(n) ? String(n) : n.toFixed(labelDecimals);
    labels.push(`${fmt(lo)}\u2013${fmt(hi)}`);
  }

  for (const v of values) {
    let idx = Math.floor((v - min) / binWidth);
    if (idx >= binCount) idx = binCount - 1; // clamp max to last bin
    bins[idx]++;
  }

  const series: Record<string, unknown> = {
    type: 'bar',
    data: bins,
    barCategoryGap: '0%',
  };

  const result: Record<string, unknown> = {
    xAxis: { type: 'category', data: labels, axisTick: { alignWithLabel: true } },
    yAxis: { type: 'value' },
    series: [series],
    tooltip: { trigger: 'axis' },
  };

  // Reference lines — the X axis above is a *category* axis (each category is a bin
  // range label), so a raw domain value (e.g. 10.5) is not a valid coordinate: ECharts
  // reads a numeric xAxis coordinate on a category axis as a bin **index**, not a data
  // value. Convert to the fractional bin-index position the value falls at instead
  // (previously every line landed at/near the same clamped edge index).
  if (Array.isArray(refLines) && refLines.length > 0) {
    series.markLine = buildMarkLine(refLines, (v) => (typeof v === 'number' ? (v - min) / binWidth : v));
  }

  return result;
}

/** Gap between adjacent segments on one row, and the narrowest drawn segment (px). */
const GANTT_SEGMENT_GAP = 1;
const GANTT_MIN_WIDTH = 2;
/** Fraction of the row band a segment fills, and the narrowest bar that still gets an in-bar label (px). */
const GANTT_BAR_FILL = 0.6;
const GANTT_LABEL_MIN_WIDTH = 24;
const GANTT_LABEL_PADDING = 4;

interface GanttItem {
  row: string;
  label?: string;
  rawStart: unknown;
  rawEnd: unknown;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Interval (Gantt) chart: rows × [start, end] segments.
 *
 * Not a bar chart with options — a floating bar needs both ends as coordinates, a row order that
 * stays as given, and per-segment separation, none of which a category-bar series expresses. Drawn
 * as a custom series whose geometry comes from the axes (`api.coord`), so zoom-free layout, axis
 * formatting and theme colors keep working as they do for the other cartesian builders.
 *
 * - Rows run top to bottom in first-appearance order (`options.categories` fixes the order and
 *   adds rows with no intervals). The category axis is inverted once so index 0 is on top.
 * - `start`/`end` are numbers (value axis) or date-like strings / timestamps (time axis).
 * - `mapping.color` splits intervals into one series per group, which gives each group a palette
 *   color and a legend entry.
 */
function buildGantt(
  data: unknown,
  mapping: NormalizedMapping | undefined,
  options: Record<string, unknown>,
): Record<string, unknown> {
  if (!Array.isArray(data) || data.length === 0) return {};
  const records = data as Record<string, unknown>[];

  const inferred = mapping ? undefined : infer('chart.gantt', records);
  const m = mapping ?? (inferred ? normalizeMapping(inferred) : undefined);
  const rowField = m?.y?.[0];
  const startField = m?.start;
  const endField = m?.end;
  if (!rowField || !startField || !endField) return {};
  const labelField = m?.label;
  const colorField = m?.color;

  // Time axis when the first present start value is a Date or a date-like string.
  const firstStart = records.map((r) => r[startField]).find((v) => v != null && v !== '');
  const isTime = firstStart instanceof Date || (typeof firstStart === 'string' && isDateLikeString(firstStart));
  const toCoord = (v: unknown): number => {
    if (v == null || v === '') return NaN;
    if (typeof v === 'number') return v;
    if (v instanceof Date) return v.getTime();
    // Day ticks sit at local midnight, so a day must too — `Date.parse` reads a date alone as UTC midnight.
    if (isTime) return parseDateText(String(v))?.getTime() ?? Date.parse(String(v));
    return Number(v);
  };

  // Row order: explicit categories first, then rows in first-appearance order.
  const rows: string[] = [];
  const rowPosition = new Map<string, number>();
  const addRow = (name: string) => {
    if (rowPosition.has(name)) return;
    rowPosition.set(name, rows.length);
    rows.push(name);
  };
  if (Array.isArray(options.categories)) {
    for (const c of options.categories as unknown[]) addRow(String(c));
  }
  for (const row of records) addRow(String(row[rowField] ?? ''));

  const groups = new Map<string, { data: Record<string, unknown>[]; items: GanttItem[] }>();
  records.forEach((row, sourceIndex) => {
    const a = toCoord(row[startField]);
    const b = toCoord(row[endField]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    const name = String(row[rowField] ?? '');
    const label = labelField != null && row[labelField] != null ? String(row[labelField]) : undefined;
    const group = colorField != null ? String(row[colorField] ?? '') : '';
    let bucket = groups.get(group);
    if (!bucket) {
      bucket = { data: [], items: [] };
      groups.set(group, bucket);
    }
    // rowIndex: the segment's row in spec.data — series split by color and skipped rows make the
    // series-local dataIndex unusable for that (uw-chart forwards it on select as _index).
    bucket.data.push({
      name: label ?? name,
      value: [rowPosition.get(name)!, Math.min(a, b), Math.max(a, b)],
      rowIndex: sourceIndex,
    });
    bucket.items.push({ row: name, label, rawStart: row[startField], rawEnd: row[endField] });
  });

  const showLabel = options.showLabel !== false;
  const refLines = options.referenceLines as ReferenceLineOption[] | undefined;
  const seriesItems: GanttItem[][] = [];

  const series = [...groups.entries()].map(([group, bucket], seriesIndex) => {
    seriesItems.push(bucket.items);
    const s: Record<string, unknown> = {
      type: 'custom',
      encode: { x: [1, 2], y: 0 },
      clip: true,
      data: bucket.data,
      renderItem: (params: { dataIndex: number }, api: {
        value: (dim: number) => unknown;
        coord: (point: number[]) => number[];
        size: (delta: number[]) => number[];
        visual: (key: string) => unknown;
      }) => {
        const row = Number(api.value(0));
        const from = api.coord([Number(api.value(1)), row]);
        const to = api.coord([Number(api.value(2)), row]);
        const height = Math.max(api.size([0, 1])[1] * GANTT_BAR_FILL, 1);
        let x = from[0] + GANTT_SEGMENT_GAP / 2;
        let width = to[0] - from[0] - GANTT_SEGMENT_GAP;
        if (width < GANTT_MIN_WIDTH) {
          x = (from[0] + to[0]) / 2 - GANTT_MIN_WIDTH / 2;
          width = GANTT_MIN_WIDTH;
        }
        const el: Record<string, unknown> = {
          type: 'rect',
          shape: { x, y: from[1] - height / 2, width, height },
          style: { fill: api.visual('color') },
        };
        const label = bucket.items[params.dataIndex]?.label;
        if (showLabel && label && width >= GANTT_LABEL_MIN_WIDTH) {
          el.textContent = {
            style: { text: label, overflow: 'truncate', width: width - GANTT_LABEL_PADDING * 2 },
          };
          el.textConfig = { position: 'inside' };
        }
        return el;
      },
    };
    if (colorField != null) s.name = group;
    if (seriesIndex === 0 && Array.isArray(refLines) && refLines.length > 0) {
      // The row axis is inverted (first row on top), which also flips a vertical line's ends:
      // 'start' is the top there, while 'end' would sit on the value axis's tick labels.
      s.markLine = buildMarkLine(refLines, (v) => (typeof v === 'number' ? v : toCoord(v)), 'start');
    }
    return s;
  });

  const xFormat = options.xFormat as AxisFormatOption | undefined;
  const endpointFormatter = xFormat ? buildAxisFormatter(xFormat, options.locale as string | undefined, isTime) : undefined;
  const endpointText = (raw: unknown): string => {
    if (!endpointFormatter) return String(raw);
    // A date string is formatted as written; a number or Date goes through the axis's own reading.
    if (typeof raw === 'string' && isTime) return endpointFormatter(raw);
    const coord = toCoord(raw);
    return Number.isFinite(coord) ? endpointFormatter(coord) : String(raw);
  };

  const result: Record<string, unknown> = {
    // A time axis picks its ticks for ECharts' own short labels; a date format (xFormat) is wider,
    // and without hideOverlap neighbouring labels print over each other.
    xAxis: isTime ? { type: 'time', axisLabel: { hideOverlap: true } } : { type: 'value' },
    yAxis: { type: 'category', data: rows, inverse: true },
    series,
    tooltip: {
      trigger: 'item',
      formatter: (p: { seriesIndex: number; dataIndex: number }) => {
        const item = seriesItems[p.seriesIndex]?.[p.dataIndex];
        if (!item) return '';
        const head = item.label != null ? `${escapeHtml(item.row)}<br/>${escapeHtml(item.label)}: ` : `${escapeHtml(item.row)}<br/>`;
        return `${head}${escapeHtml(endpointText(item.rawStart))} → ${escapeHtml(endpointText(item.rawEnd))}`;
      },
    },
  };
  if (colorField != null && series.length > 0) {
    result.legend = { data: series.map((s) => s.name as string) };
  }
  return result;
}

function getNumberFields(data: Record<string, unknown>[]): string[] {
  if (data.length === 0) return [];
  const first = data[0];
  return Object.keys(first).filter((k) => typeof first[k] === 'number');
}

/**
 * Deep-merge `overrides` into `base` recursively.
 * If both base[key] and overrides[key] are plain objects, merge recursively.
 * Arrays and other values: overrides[key] wins.
 */
function deepMerge(
  base: Record<string, unknown>,
  overrides: Record<string, unknown>,
  elementwise: ReadonlySet<string> = new Set(),
): Record<string, unknown> {
  const result = { ...base };
  for (const key of Object.keys(overrides)) {
    const bv = base[key];
    const ov = overrides[key];
    if (isPlainObject(bv) && isPlainObject(ov)) {
      result[key] = deepMerge(bv as Record<string, unknown>, ov as Record<string, unknown>);
    } else if (elementwise.has(key) && Array.isArray(ov) && bv != null) {
      result[key] = mergeElementwise(bv, ov);
    } else {
      result[key] = ov;
    }
  }
  return result;
}

/**
 * Passthrough keys whose arrays line up with the generated ones — `series[i]`, `xAxis[i]`, `yAxis[i]`. Replacing them
 * whole dropped the builder's data and the reference-line markLine (`series: [{ type: 'line' }]` drew an empty chart).
 */
const ELEMENTWISE_KEYS: ReadonlySet<string> = new Set(['series', 'xAxis', 'yAxis']);

/** `override[i]` merged onto `base[i]`; extra override entries are appended. A generated single axis object stays one. */
function mergeElementwise(base: unknown, override: unknown[]): unknown {
  const items = [base].flat();
  const merged = override
    .map((o, i) => (isPlainObject(items[i]) && isPlainObject(o) ? deepMerge(items[i], o) : o))
    .concat(items.slice(override.length));
  return Array.isArray(base) || merged.length > 1 ? merged : merged[0];
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/** A timestamp as a local wall-clock ISO string (`YYYY-MM-DDTHH:mm:ss.sss`) — no UTC shift. */
function localIsoString(ms: number): string {
  const offset = new Date(ms).getTimezoneOffset() * 60_000;
  return new Date(ms - offset).toISOString().slice(0, 23);
}

/**
 * Build a formatter string or function for ECharts axis labels
 * based on an AxisFormatOption.
 *
 * `timestamps`: the axis is a time axis, so a numeric label value is epoch milliseconds. Date
 * formats read it as local wall-clock time — `formatValue` alone passes numbers through as-is, and
 * a UTC ISO string would label a local-midnight tick with the previous day east of UTC.
 */
function buildAxisFormatter(fmt: AxisFormatOption, locale?: string, timestamps = false): (value: number | string) => string {
  const isDateFormat = fmt.type === 'date' || fmt.type === 'datetime' || fmt.type === 'time';
  return (raw: number | string) => {
    const value = timestamps && isDateFormat && typeof raw === 'number' && Number.isFinite(raw)
      ? localIsoString(raw)
      : raw;
    let result: string;
    // Compact notation short-circuits formatValue — Intl.NumberFormat handles both
    // number and currency compact formatting (e.g. `₩1.2억`, `$1.2M`).
    if (fmt.compact && (fmt.type === 'number' || fmt.type === 'currency')) {
      const num = Number(value);
      if (!isNaN(num)) {
        const nfOpts: Intl.NumberFormatOptions = {
          notation: 'compact',
          compactDisplay: 'short',
          maximumFractionDigits: fmt.decimals ?? 1,
        };
        if (fmt.type === 'currency' && fmt.currency) {
          nfOpts.style = 'currency';
          nfOpts.currency = fmt.currency;
        }
        result = new Intl.NumberFormat(locale, nfOpts).format(num);
      } else {
        result = String(value);
      }
      if (fmt.prefix) result = fmt.prefix + result;
      if (fmt.suffix) result = result + fmt.suffix;
      return result;
    }
    if (fmt.type) {
      const formatStr = fmt.type === 'currency' && fmt.currency
        ? `currency:${fmt.currency}`
        : fmt.type;
      result = formatValue(value, formatStr, locale);
      // For percent type, formatValue divides by 100 — but axis values are already in
      // display units (e.g. 73 means 73%), so use raw suffix instead
      if (fmt.type === 'percent') {
        result = typeof value === 'number'
          ? new Intl.NumberFormat(locale, { maximumFractionDigits: fmt.decimals ?? 2 }).format(value) + '%'
          : String(value) + '%';
      }
    } else {
      result = String(value);
    }
    if (fmt.decimals !== undefined && fmt.type === 'number') {
      const num = Number(value);
      if (!isNaN(num)) {
        result = new Intl.NumberFormat(locale, {
          minimumFractionDigits: fmt.decimals,
          maximumFractionDigits: fmt.decimals,
        }).format(num);
      }
    }
    if (fmt.prefix) result = fmt.prefix + result;
    if (fmt.suffix) result = result + fmt.suffix;
    return result;
  };
}

/**
 * A number in the locale, with as many fraction digits as the value itself carries — an axis tick
 * of 0.25 stays 0.25 (`0,25`), never rounded to the Intl default of three.
 */
function localeNumberFormatter(locale?: string): (value: number) => string {
  const cache = new Map<number, Intl.NumberFormat>();
  return (value: number) => {
    const text = String(value);
    if (/e/i.test(text)) return text;
    const digits = Math.min(text.split('.')[1]?.length ?? 0, 20);
    let nf = cache.get(digits);
    if (!nf) {
      nf = new Intl.NumberFormat(locale, { maximumFractionDigits: digits });
      cache.set(digits, nf);
    }
    return nf.format(value);
  };
}

/**
 * Locale defaults for numbers ECharts would otherwise write itself:
 * - a `value` / `log` axis without a label formatter (xFormat · yFormat · valueAxisFormat set one);
 * - a tooltip without `formatter` / `valueFormatter`, unless a time axis is present (its values are
 *   timestamps that ECharts formats as dates).
 * Runs before the `options.echarts` passthrough, so a formatter the spec sets there still wins.
 */
function applyLocaleNumberDefaults(result: Record<string, unknown>, locale?: string): void {
  const { axisFormatter, valueFormatter } = localeFormatters(locale);
  let hasTimeAxis = false;

  for (const key of ['xAxis', 'yAxis'] as const) {
    const axis = result[key];
    const withDefault = (a: unknown): unknown => {
      if (!isPlainObject(a)) return a;
      if (a.type === 'time') hasTimeAxis = true;
      if (a.type !== 'value' && a.type !== 'log') return a;
      const label = isPlainObject(a.axisLabel) ? a.axisLabel : {};
      if (label.formatter !== undefined) return a;
      return { ...a, axisLabel: { ...label, formatter: axisFormatter } };
    };
    if (Array.isArray(axis)) result[key] = axis.map(withDefault);
    else if (axis) result[key] = withDefault(axis);
  }

  const tooltip = result.tooltip;
  if (!isPlainObject(tooltip) || hasTimeAxis) return;
  if (tooltip.formatter !== undefined || tooltip.valueFormatter !== undefined) return;
  result.tooltip = { ...tooltip, valueFormatter };
}

interface LocaleFormatters {
  axisFormatter: (value: number | string) => string;
  valueFormatter: (value: unknown) => string | string[];
}

/** One pair per locale — the same spec yields the same option, functions included. */
const LOCALE_FORMATTERS = new Map<string, LocaleFormatters>();

function localeFormatters(locale?: string): LocaleFormatters {
  const key = locale ?? '';
  let pair = LOCALE_FORMATTERS.get(key);
  if (!pair) {
    const format = localeNumberFormatter(locale);
    const readable = (v: unknown): string =>
      typeof v === 'number' ? (Number.isFinite(v) ? format(v) : '-') : v == null || v === '' ? '-' : String(v);
    pair = {
      axisFormatter: (value) => (typeof value === 'number' ? format(value) : String(value)),
      valueFormatter: (value) => (Array.isArray(value) ? value.map(readable) : readable(value)),
    };
    LOCALE_FORMATTERS.set(key, pair);
  }
  return pair;
}

/**
 * Apply xFormat/yFormat to the corresponding axis in the ECharts option.
 * Handles both single axis and axis arrays (dual Y axis).
 */
function applyAxisFormat(
  result: Record<string, unknown>,
  axisKey: 'xAxis' | 'yAxis',
  fmt: AxisFormatOption | undefined,
  locale?: string,
): void {
  if (!fmt || !result[axisKey]) return;

  const axis = result[axisKey];
  const formatter = buildAxisFormatter(fmt, locale, isPlainObject(axis) && axis.type === 'time');

  if (Array.isArray(axis)) {
    // Dual axis — apply to all value-type axes
    result[axisKey] = (axis as Record<string, unknown>[]).map((a) => {
      if (a.type === 'value') {
        return { ...a, axisLabel: { ...(a.axisLabel as Record<string, unknown> ?? {}), formatter } };
      }
      return a;
    });
  } else if (isPlainObject(axis)) {
    const a = axis as Record<string, unknown>;
    result[axisKey] = { ...a, axisLabel: { ...(a.axisLabel as Record<string, unknown> ?? {}), formatter } };
  }
}
